import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ProductGallery from './components/ProductGallery';
import ProductInfo from './components/ProductInfo';
import ProductTabs from './components/ProductTabs';
import QuoteModal from './components/QuoteModal';
import RelatedProducts from './components/RelatedProducts';
import Icon from '../../components/AppIcon';
import { getJson } from '../../api/client';

const ProductDetail = () => {
  const { idOrSlug: routeIdOrSlug } = useParams();
  const [searchParams] = useSearchParams();

  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [productData, setProductData] = useState(null);
  const [apiRelatedProducts, setApiRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mock product data (kept as rich fallback for SPATZ3)
  const product = {
    id: "spatz3-balloon",
    name: "SPATZ3 Adjustable Intragastric Balloon",
    shortDescription: "The world\'s first and only adjustable intragastric balloon system for effective weight management",
    category: "Obesity Management",
    manufacturer: "Spatz Medical",
    sku: "SPZ-IGB-001",
    price: 15000,
    originalPrice: 18000,
    availability: "In Stock",
    isNew: true,
    mainImage: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=800&fit=crop",
    galleryImages: [
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=800&fit=crop",
      "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=800&h=800&fit=crop",
      "https://images.unsplash.com/photo-1584362917165-526a968579e8?w=800&h=800&fit=crop"
    ],
    keyFeatures: [
      "World\'s first adjustable intragastric balloon",
      "Volume adjustable from 400ml to 900ml",
      "12-month implantation period",
      "Minimally invasive endoscopic procedure",
      "FDA approved and CE certified",
      "Proven weight loss results up to 25% excess weight"
    ],
    certifications: [
      "GDPMD Certified",
      "FDA Approved",
      "CE Mark",
      "ISO 13485"
    ],
    quickSpecs: [
      { label: "Balloon Volume", value: "400-900ml adjustable" },
      { label: "Implantation Period", value: "12 months" },
      { label: "Procedure Type", value: "Endoscopic" },
      { label: "Material", value: "Medical grade silicone" },
      { label: "Filling Solution", value: "Saline with methylene blue" }
    ],
    specifications: [
      {
        category: "Technical Specifications",
        items: [
          { label: "Balloon Material", value: "Medical grade silicone elastomer" },
          { label: "Catheter Material", value: "Polyurethane" },
          { label: "Balloon Volume Range", value: "400ml - 900ml" },
          { label: "Filling Solution", value: "Sterile saline with methylene blue" },
          { label: "Shelf Life", value: "3 years from manufacture date" },
          { label: "Storage Temperature", value: "15°C to 30°C" }
        ]
      },
      {
        category: "Clinical Parameters",
        items: [
          { label: "Implantation Duration", value: "Up to 12 months" },
          { label: "Expected Weight Loss", value: "15-25% excess body weight" },
          { label: "BMI Range", value: "27-40 kg/m²" },
          { label: "Procedure Time", value: "15-20 minutes" },
          { label: "Recovery Time", value: "24-48 hours" },
          { label: "Follow-up Schedule", value: "1, 3, 6, 9, 12 months" }
        ]
      }
    ],
    clinicalStudies: [
      {
        title: "SPATZ3 Adjustable Balloon System: 12-Month Multi-Center Study",
        journal: "Obesity Surgery Journal",
        year: "2023",
        participants: 247,
        summary: `A comprehensive multi-center study evaluating the safety and efficacy of the SPATZ3 adjustable intragastric balloon system over 12 months. The study demonstrated significant weight loss with minimal adverse events.`,
        keyFindings: [
          { value: "23.4%", metric: "Average excess weight loss" },
          { value: "89%", metric: "Patient satisfaction rate" },
          { value: "2.1%", metric: "Serious adverse events" }
        ]
      },
      {
        title: "Adjustable vs Fixed Volume Intragastric Balloons: Comparative Analysis",
        journal: "International Journal of Obesity",
        year: "2022",
        participants: 156,
        summary: `Comparative study between adjustable SPATZ3 balloon and fixed-volume balloons, showing superior weight loss outcomes and patient tolerance with the adjustable system.`,
        keyFindings: [
          { value: "31%", metric: "Better weight loss vs fixed" },
          { value: "45%", metric: "Reduced early removals" },
          { value: "92%", metric: "Completion rate" }
        ]
      }
    ],
    manuals: [
      {
        title: "SPATZ3 Implantation Guide",
        type: "pdf",
        size: "2.4 MB",
        lastUpdated: "15/11/2024"
      },
      {
        title: "Patient Information Booklet",
        type: "pdf",
        size: "1.8 MB",
        lastUpdated: "10/11/2024"
      },
      {
        title: "Adjustment Procedure Video",
        type: "video",
        size: "45 MB",
        lastUpdated: "05/11/2024"
      },
      {
        title: "Troubleshooting Manual",
        type: "pdf",
        size: "3.1 MB",
        lastUpdated: "01/11/2024"
      }
    ],
    relatedProducts: [
      {
        id: "hanarostent-esophagus",
        name: "Hanarostent® Esophagus Stent",
        description: "Self-expanding metal stent for esophageal strictures",
        image: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=400&h=400&fit=crop",
        price: 8500,
        category: "Endoscopy"
      },
      {
        id: "polypectomy-snare",
        name: "360° Rotation Polypectomy Snare",
        description: "Advanced snare with 360-degree rotation capability",
        image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=400&fit=crop",
        price: 450,
        category: "Surgery"
      },
      {
        id: "peg-kit",
        name: "Complete PEG Kit",
        description: "Comprehensive percutaneous endoscopic gastrostomy kit",
        image: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&h=400&fit=crop",
        price: 1200,
        category: "Endoscopy"
      }
    ]
  };

  const effectiveProduct = productData || product;

  const breadcrumbItems = [
    { label: 'Home', path: '/homepage' },
    { label: 'Products', path: '/product-portfolio' },
    effectiveProduct?.category
      ? {
          label: effectiveProduct.category,
          path: `/product-portfolio?category=${encodeURIComponent(
            effectiveProduct.category
          )}`,
        }
      : null,
    effectiveProduct
      ? {
          label: effectiveProduct.name,
          path: `/product-detail/${effectiveProduct.slug || effectiveProduct.id}`,
          isLast: true,
        }
      : { label: 'Product Detail', path: '/product-detail', isLast: true },
  ].filter(Boolean);

  const handleRequestQuote = (productForQuote, quantity) => {
    setSelectedQuantity(quantity);
    setIsQuoteModalOpen(true);
  };

  const handleCloseQuoteModal = () => {
    setIsQuoteModalOpen(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const idOrSlug =
      routeIdOrSlug || searchParams.get('id') || product.id || 'spatz3-balloon';

    let isMounted = true;

    const loadProduct = async () => {
      setLoading(true);
      try {
        const detail = await getJson(`/products/${encodeURIComponent(idOrSlug)}`);
        if (!isMounted) return;

        setProductData(detail);

        // Related products from API (same category, excluding current)
        try {
          const related = await getJson(`/products/${encodeURIComponent(idOrSlug)}/related`);
          if (!isMounted) return;
          setApiRelatedProducts(Array.isArray(related) ? related : []);
        } catch (relatedErr) {
          console.error('Failed to load related products', relatedErr);
          setApiRelatedProducts([]);
        }

        setError(null);
      } catch (err) {
        console.error('Failed to load product from API, using mock detail.', err);
        if (!isMounted) return;
        setProductData(null);
        setApiRelatedProducts([]);
        setError('Unable to load product details from server. Showing reference product.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      isMounted = false;
    };
  }, [routeIdOrSlug, searchParams]);

  const isReady = !!effectiveProduct && !loading;

  return (
    <div className="min-h-screen bg-background">
        {/* Breadcrumb */}
        <div className="bg-muted border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Breadcrumb customItems={breadcrumbItems} />
          </div>
        </div>

        {/* Loading / Error / Empty States */}
        {loading && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center text-text-secondary">Loading product details...</div>
          </div>
        )}

        {!loading && error && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="text-center text-warning text-sm">{error}</div>
          </div>
        )}

        {isReady && (
          <>
            {/* Product Detail Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Product Gallery */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  className="lg:col-span-7"
                >
                  <ProductGallery product={effectiveProduct} />
                </motion.div>

                {/* Product Information */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="lg:col-span-5"
                >
                  <ProductInfo
                    product={effectiveProduct}
                    onRequestQuote={handleRequestQuote}
                  />
                </motion.div>
              </div>
            </div>

            {/* Product Tabs */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <ProductTabs product={effectiveProduct} relatedProducts={apiRelatedProducts} />
              </motion.div>
            </div>

            {/* Related Products */}
            {apiRelatedProducts.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="border-t border-border"
              >
                <RelatedProducts products={apiRelatedProducts} />
              </motion.div>
            )}
          </>
        )}

        {/* Trust Indicators */}
        <div className="bg-muted border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={24} className="text-success" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary">GDPMD Certified</h4>
                  <p className="text-sm text-text-secondary">Malaysian medical device compliance</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Truck" size={24} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary">Fast Delivery</h4>
                  <p className="text-sm text-text-secondary">Same-day dispatch for in-stock items</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Headphones" size={24} className="text-warning" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary">Expert Support</h4>
                  <p className="text-sm text-text-secondary">Technical support & training included</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      {/* Quote Modal */}
      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={handleCloseQuoteModal}
        product={effectiveProduct}
        quantity={selectedQuantity}
      />
    </div>
  );
};

export default ProductDetail;